
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Zap, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

const TestBuzzzSender = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  const sendTestBuzzz = async () => {
    if (!user) {
      toast.error("You must be logged in to receive test buzzz");
      return;
    }

    setLoading(true);
    setResults([]);

    try {
      // Get all test users
      const { data: testUsers, error: testUsersError } = await supabase
        .from('test_users')
        .select('user_id, username')
        .limit(10);

      if (testUsersError) throw testUsersError;

      if (!testUsers || testUsers.length === 0) {
        toast.error("No test users found. Generate some test users first.");
        return;
      }

      console.log(`Found ${testUsers.length} test users, sending buzzz to current user: ${user.id}`);

      const buzzzResults = [];

      // Send buzzz from each test user to current user
      for (const testUser of testUsers) {
        try {
          const { error: buzzzError } = await supabase
            .from('interactions')
            .insert({
              user_id: testUser.user_id,
              target_user_id: user.id,
              interaction_type: 'buzzz'
            });

          if (buzzzError) {
            console.log(`Failed to send buzzz from ${testUser.username}:`, buzzzError);
            buzzzResults.push({
              username: testUser.username,
              success: false,
              error: buzzzError.message
            });
          } else {
            console.log(`✅ Buzzz sent from ${testUser.username} to current user`);
            buzzzResults.push({
              username: testUser.username,
              success: true
            });
          }
        } catch (error: any) {
          console.error(`Error sending buzzz from ${testUser.username}:`, error);
          buzzzResults.push({
            username: testUser.username,
            success: false,
            error: error.message
          });
        }
      }

      setResults(buzzzResults);
      
      const successCount = buzzzResults.filter(r => r.success).length;
      const failCount = buzzzResults.filter(r => !r.success).length;

      toast.success(`Sent ${successCount} buzzz successfully! ${failCount > 0 ? `${failCount} failed.` : ''}`);

    } catch (error: any) {
      console.error('Error sending test buzzz:', error);
      toast.error('Failed to send test buzzz');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-neon-red" />
          Test Buzzz Sender
        </CardTitle>
        <CardDescription>
          Send a buzzz from each test user to the current logged-in user for testing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Send Test Buzzz</h3>
            <p className="text-sm text-muted-foreground">
              Each test user will send 1 buzzz to you. Check the Buzzz page to see results.
            </p>
          </div>
          <Button 
            onClick={sendTestBuzzz} 
            disabled={loading || !user}
            className="min-w-[120px] bg-neon-red hover:bg-neon-red/80"
          >
            <Zap className="w-4 h-4 mr-2" />
            {loading ? 'Sending...' : 'Send Buzzz'}
          </Button>
        </div>

        {results.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium">Results:</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {results.map((result, index) => (
                <div 
                  key={index} 
                  className={`flex items-center justify-between p-2 rounded text-sm ${
                    result.success 
                      ? 'bg-green-50 text-green-800' 
                      : 'bg-red-50 text-red-800'
                  }`}
                >
                  <span>{result.username}</span>
                  <span>
                    {result.success ? '✅ Success' : `❌ ${result.error}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!user && (
          <div className="p-4 bg-yellow-50 text-yellow-800 rounded-lg text-sm">
            You must be logged in to receive test buzzz
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TestBuzzzSender;
